import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Users as UsersIcon, 
  UserPlus, 
  Shield, 
  Activity, 
  Clock, 
  Mail, 
  Settings,
  RefreshCw,
  Search,
  Filter,
  MoreVertical
} from 'lucide-react';
import { apiClient } from '../../services/api-client';

interface User {
  id: string;
  username: string;
  email: string;
  role: string;
  status: 'active' | 'inactive' | 'suspended';
  last_login: string;
  created_at: string;
  permissions: string[];
  avatar?: string;
}

interface Role {
  id: string;
  name: string;
  description: string;
  permissions: string[];
  user_count: number;
}

interface Permission {
  id: string;
  name: string;
  description: string;
  category: string;
}

interface Session {
  id: string;
  user_id: string;
  username: string;
  ip_address: string;
  user_agent: string;
  created_at: string;
  last_activity: string;
  status: 'active' | 'expired';
}

interface UserStats {
  total_users: number;
  active_users: number;
  online_users: number;
  new_users_today: number;
  roles_count: number;
  permissions_count: number;
}

const Users: React.FC<{ className?: string }> = ({ className }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [sessions, setSessions] = useState<Session[]>([]);
  const [stats, setStats] = useState<UserStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('all');
  const [activeTab, setActiveTab] = useState<'users' | 'roles' | 'permissions' | 'sessions'>('users');

  // Load all user data
  const loadUserData = async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Fetch all user data in parallel
      const [usersResponse, rolesResponse, permissionsResponse, sessionsResponse, statsResponse] = await Promise.allSettled([
        apiClient.get<{ success: boolean; data: User[] }>('/api/v1/users/'),
        apiClient.get<{ success: boolean; data: Role[] }>('/api/v1/users/roles'),
        apiClient.get<{ success: boolean; data: Permission[] }>('/api/v1/users/permissions'),
        apiClient.get<{ success: boolean; data: Session[] }>('/api/v1/users/sessions'),
        apiClient.get<{ success: boolean; data: UserStats }>('/api/v1/users/stats'),
      ]);

      // Handle users data
      if (usersResponse.status === 'fulfilled' && usersResponse.value.success) {
        setUsers(usersResponse.value.data);
      }

      // Handle roles data
      if (rolesResponse.status === 'fulfilled' && rolesResponse.value.success) {
        setRoles(rolesResponse.value.data);
      }

      // Handle permissions data
      if (permissionsResponse.status === 'fulfilled' && permissionsResponse.value.success) {
        setPermissions(permissionsResponse.value.data);
      }

      // Handle sessions data
      if (sessionsResponse.status === 'fulfilled' && sessionsResponse.value.success) {
        setSessions(sessionsResponse.value.data);
      }

      // Handle stats data
      if (statsResponse.status === 'fulfilled' && statsResponse.value.success) {
        setStats(statsResponse.value.data);
      }

    } catch (err) {
      console.error('Error loading user data:', err);
      setError('Failed to load user data');
    } finally {
      setLoading(false);
    }
  };

  // Load data on mount and refresh every 60 seconds
  useEffect(() => {
    loadUserData();
    const interval = setInterval(loadUserData, 60000);
    return () => clearInterval(interval);
  }, []);

  // Filter users based on search and role
  const filteredUsers = Array.isArray(users) ? users.filter(user => {
    const matchesSearch = user.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         user.email.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesRole = selectedRole === 'all' || user.role === selectedRole;
    return matchesSearch && matchesRole;
  }) : [];

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500';
      case 'inactive':
        return 'bg-gray-500';
      case 'suspended':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getRoleColor = (role: string) => {
    if (!role) return 'bg-gray-500';
    
    switch (role.toLowerCase()) {
      case 'admin':
        return 'bg-red-500';
      case 'operator':
        return 'bg-blue-500';
      case 'user':
        return 'bg-green-500';
      default:
        return 'bg-gray-500';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  const getTimeAgo = (dateString: string) => {
    const now = new Date();
    const date = new Date(dateString);
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  if (loading) {
    return (
      <div className={`users-content ${className || ''}`}>
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading user data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`users-content ${className || ''}`}>
        <div className="text-center py-8">
          <div className="text-red-500 text-4xl mb-4">⚠️</div>
          <h3 className="text-lg font-semibold mb-2">Error Loading User Data</h3>
          <p className="text-gray-600 mb-4">{error}</p>
          <Button onClick={loadUserData} className="flex items-center gap-2">
            <RefreshCw className="h-4 w-4" />
            Retry
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className={`users-content ${className || ''}`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">User Management</h1>
          <p className="text-gray-600">Manage users, roles, and permissions</p>
        </div>
        <Button onClick={loadUserData} className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.total_users}</p>
                </div>
                <UsersIcon className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Active Users</p>
                  <p className="text-2xl font-bold text-green-600">{stats.active_users}</p>
                </div>
                <Activity className="h-8 w-8 text-green-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Online Now</p>
                  <p className="text-2xl font-bold text-blue-600">{stats.online_users}</p>
                </div>
                <Clock className="h-8 w-8 text-blue-500" />
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">New Today</p>
                  <p className="text-2xl font-bold text-purple-600">{stats.new_users_today}</p>
                </div>
                <UserPlus className="h-8 w-8 text-purple-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Tabs */}
      <div className="flex space-x-1 bg-gray-100 p-1 rounded-lg mb-6">
        <Button
          variant={activeTab === 'users' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('users')}
          className="flex items-center gap-2"
        >
          <UsersIcon className="h-4 w-4" />
          Users ({Array.isArray(users) ? users.length : 0})
        </Button>
        <Button
          variant={activeTab === 'roles' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('roles')}
          className="flex items-center gap-2"
        >
          <Shield className="h-4 w-4" />
          Roles ({Array.isArray(roles) ? roles.length : 0})
        </Button>
        <Button
          variant={activeTab === 'permissions' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('permissions')}
          className="flex items-center gap-2"
        >
          <Settings className="h-4 w-4" />
          Permissions ({Array.isArray(permissions) ? permissions.length : 0})
        </Button>
        <Button
          variant={activeTab === 'sessions' ? 'primary' : 'ghost'}
          size="sm"
          onClick={() => setActiveTab('sessions')}
          className="flex items-center gap-2"
        >
          <Activity className="h-4 w-4" />
          Sessions ({Array.isArray(sessions) ? sessions.length : 0})
        </Button>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'users' && (
        <div>
          {/* Search and Filter */}
          <div className="flex gap-4 mb-6">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search users..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="all">All Roles</option>
              {Array.isArray(roles) ? roles.map(role => (
                <option key={role.id} value={role.name}>{role.name}</option>
              )) : null}
            </select>
          </div>

          {/* Users List */}
          <div className="grid gap-4">
            {filteredUsers.map(user => (
              <Card key={user.id}>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-4">
                      <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.username} className="w-10 h-10 rounded-full" />
                        ) : (
                          <span className="text-gray-600 font-semibold">
                            {user.username.charAt(0).toUpperCase()}
                          </span>
                        )}
                      </div>
                      <div>
                        <h3 className="font-semibold text-gray-900">{user.username}</h3>
                        <p className="text-sm text-gray-600">{user.email}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge className={`${getRoleColor(user.role)} text-white`}>
                            {user.role}
                          </Badge>
                          <Badge className={`${getStatusColor(user.status)} text-white`}>
                            {user.status}
                          </Badge>
                        </div>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-600">Last login</p>
                      <p className="text-sm font-medium">{getTimeAgo(user.last_login)}</p>
                      <Button variant="ghost" size="sm" className="mt-2">
                        <MoreVertical className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {activeTab === 'roles' && (
        <div className="grid gap-4">
          {Array.isArray(roles) ? roles.map(role => (
            <Card key={role.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{role.name}</h3>
                    <p className="text-sm text-gray-600">{role.description}</p>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {role.permissions.slice(0, 3).map(permission => (
                        <Badge key={permission} variant="outline" className="text-xs">
                          {permission}
                        </Badge>
                      ))}
                      {role.permissions.length > 3 && (
                        <Badge variant="outline" className="text-xs">
                          +{role.permissions.length - 3} more
                        </Badge>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Users</p>
                    <p className="text-lg font-semibold text-blue-600">{role.user_count}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )) : null}
        </div>
      )}

      {activeTab === 'permissions' && (
        <div className="grid gap-4">
          {Array.isArray(permissions) ? permissions.map(permission => (
            <Card key={permission.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{permission.name}</h3>
                    <p className="text-sm text-gray-600">{permission.description}</p>
                    <Badge variant="outline" className="mt-2">
                      {permission.category}
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          )) : null}
        </div>
      )}

      {activeTab === 'sessions' && (
        <div className="grid gap-4">
          {Array.isArray(sessions) ? sessions.map(session => (
            <Card key={session.id}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-gray-900">{session.username}</h3>
                    <p className="text-sm text-gray-600">IP: {session.ip_address}</p>
                    <p className="text-sm text-gray-600 truncate max-w-xs">{session.user_agent}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <Badge className={`${session.status === 'active' ? 'bg-green-500' : 'bg-gray-500'} text-white`}>
                        {session.status}
                      </Badge>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-sm text-gray-600">Last activity</p>
                    <p className="text-sm font-medium">{getTimeAgo(session.last_activity)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          )) : null}
        </div>
      )}
    </div>
  );
};

export default Users; 